import React, { useState } from 'react';
import { X, Send, Check, CheckCheck } from 'lucide-react';

const WhatsAppWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');

  // Your WhatsApp Business number
  const phoneNumber = '919052808055'; // Format: country code + number (no + or spaces)
  
  // Pre-filled message templates
  const quickMessages = [
    "Hi, I'm interested in studying abroad. Can you help?",
    "I need help with student visa application",
    "What are the study abroad requirements?",
    "I want to book a free consultation"
  ];

  const handleSendMessage = (customMessage = null) => {
    const messageToSend = customMessage || message || quickMessages[0];
    const encodedMessage = encodeURIComponent(messageToSend);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    setIsOpen(false);
    setMessage('');
  };

  const handleQuickMessage = (msg) => {
    handleSendMessage(msg);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Chat Window */}
      {isOpen && (
        <div className="mb-4 w-80 md:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-fade-in">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#25D366] to-[#128C7E] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d7a39a59-63ba-4bd9-b746-de38b35bd039/artifacts/rsx786qh_WhatsApp%20Image%202026-04-08%20at%2017.50.03.jpeg"
                  alt="Visaspot" 
                  className="w-12 h-12 rounded-full border-2 border-white object-cover bg-white p-1"
                />
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
              </div>
              <div>
                <h3 className="text-white font-bold">Visaspot</h3>
                <p className="text-white/90 text-xs flex items-center gap-1">
                  <span className="inline-block w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  Online • Replies instantly
                </p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)} 
              className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Chat Content */}
          <div className="p-4 bg-[#ECE5DD] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iYSIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVHJhbnNmb3JtPSJyb3RhdGUoNDUpIj48cGF0aCBkPSJNLS4wMSA0MC4wMWw0MC4wMi00MC4wMiIgc3Ryb2tlPSIjMDAwIiBzdHJva2Utd2lkdGg9Ii4wNSIgb3BhY2l0eT0iLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjYSkiLz48L3N2Zz4=')] min-h-[300px] max-h-[400px] overflow-y-auto">
            {/* Welcome Message */}
            <div className="mb-4">
              <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-md max-w-[85%] inline-block">
                <p className="text-sm text-gray-800 mb-2">
                  👋 Hello! Welcome to <span className="font-bold">Visaspot</span>
                </p>
                <p className="text-sm text-gray-700">
                  How can we help you with your study abroad journey today?
                </p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs text-gray-400">Just now</span>
                  <CheckCheck size={14} className="text-blue-500" />
                </div>
              </div>
            </div>

            {/* Quick Reply Buttons */}
            <div className="space-y-2 mb-4">
              <p className="text-xs text-gray-600 mb-2">Quick replies:</p>
              {quickMessages.map((msg, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickMessage(msg)}
                  className="w-full bg-white hover:bg-gray-50 text-left px-4 py-3 rounded-lg shadow-sm border border-gray-200 text-sm text-gray-700 transition-all duration-200 hover:shadow-md group"
                >
                  <div className="flex items-center justify-between">
                    <span>{msg}</span>
                    <Send size={14} className="text-gray-400 group-hover:text-[#25D366] transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Type your message..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent text-sm"
              />
              <button
                onClick={() => handleSendMessage()}
                className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full p-3 transition-all duration-300 hover:scale-110 shadow-lg"
              >
                <Send size={20} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              We'll reply on WhatsApp within minutes
            </p>
          </div>
        </div>
      )}

      {/* WhatsApp Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-16 h-16 rounded-full bg-[#25D366] text-white shadow-2xl hover:shadow-[#25D366]/50 flex items-center justify-center hover:scale-110 transition-all duration-300 relative group"
        aria-label="Chat on WhatsApp"
      >
        {isOpen ? (
          <X size={28} />
        ) : (
          <>
            {/* WhatsApp Icon */}
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-9 h-9">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            
            {/* Notification Badge */}
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
              1
            </span>
            
            {/* Ripple Effect */}
            <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20"></span>
          </>
        )}
      </button>

      {/* Tooltip */}
      {!isOpen && (
        <div className="absolute bottom-20 right-0 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          Chat with us on WhatsApp
          <div className="absolute -bottom-1 right-6 w-2 h-2 bg-gray-900 transform rotate-45"></div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppWidget;
